import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { articles, type ArticleSummary } from "@/mocks/articles";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.id === slug);

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles: ArticleSummary[] = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3)
    .map(({ id, title, excerpt, categorySlug, categoryName, author, date, coverImage, tags, readTime }) => ({
      id, title, excerpt, categorySlug, categoryName, author, date, coverImage, tags, readTime,
    }));

  if (relatedArticles.length < 3) {
    const extraArticles: ArticleSummary[] = articles
      .filter((a) => a.id !== article.id && a.categorySlug !== article.categorySlug)
      .slice(0, 3 - relatedArticles.length)
      .map(({ id, title, excerpt, categorySlug, categoryName, author, date, coverImage, tags, readTime }) => ({
        id, title, excerpt, categorySlug, categoryName, author, date, coverImage, tags, readTime,
      }));
    relatedArticles.push(...extraArticles);
  }

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.coverImage,
    "author": {
      "@type": "Person",
      "name": article.author,
    },
    "datePublished": article.date,
    "publisher": {
      "@type": "Organization",
      "name": "黄油网站",
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://huangyou.design/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://huangyou.design/" },
      { "@type": "ListItem", "position": 2, "name": article.categoryName, "item": `https://huangyou.design/category/${article.categorySlug}` },
      { "@type": "ListItem", "position": 3, "name": article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}></script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <article>
            {/* Header */}
            <header className="w-full py-8 md:py-12 bg-background-50 border-b border-background-200/70">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <Breadcrumb items={[
                  { label: t("article.breadcrumb_home"), to: "/" },
                  { label: article.categoryName, to: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]} />

                <div className="mt-4 md:mt-6">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="inline-block text-xs px-2.5 py-1 rounded-full mb-3 no-underline cursor-pointer transition-colors"
                    style={{ backgroundColor: "oklch(var(--accent-100))", color: "oklch(var(--accent-700))" }}
                  >
                    {article.categoryName}
                  </Link>
                  <h1 className="text-xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4 md:mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i> {article.author}
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i> {article.date}
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i> {article.readTime}
                    </span>
                  </div>
                </div>
              </div>
            </header>

            {/* Cover Image */}
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6 mt-8">
              <div className="w-full aspect-[16/9] rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content */}
            <section className="w-full py-8 md:py-12">
              <div className="max-w-3xl mx-auto px-4 md:px-6">
                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                  style={{ fontFamily: "var(--font-body)" }}
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Tags */}
                <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200/70">
                  <span className="text-sm text-foreground-500 mr-1">{t("article.tags")}:</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${article.categorySlug}`}
                      className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors no-underline cursor-pointer"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                {/* Author Box */}
                <div className="flex items-center gap-3 p-4 rounded-lg mt-6" style={{ backgroundColor: "oklch(var(--secondary-50))" }}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ backgroundColor: "oklch(var(--accent-100))" }}>
                    <i className="ri-user-line text-lg" style={{ color: "oklch(var(--accent-500))" }}></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{article.author}</p>
                    <p className="text-xs text-foreground-500">黄油网站 {article.categoryName}专栏作者</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Articles */}
            <section className="w-full py-10 md:py-14" style={{ backgroundColor: "oklch(var(--secondary-50))" }}>
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h3 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 md:mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                  {t("article.related")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-background-300/60 transition-all duration-300 no-underline cursor-pointer flex flex-col"
                    >
                      <div className="relative w-full aspect-[16/10] overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <span className="text-xs text-foreground-500 mb-2">{ra.categoryName}</span>
                        <h4 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-foreground-800 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed flex-1">
                          {ra.excerpt}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-foreground-400 mt-3 pt-3 border-t border-background-200/70">
                          <span>{ra.author}</span>
                          <span>·</span>
                          <span>{ra.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}